import { useMemo, useState } from 'react';
import { BarChart, LineChart } from '../components/Chart';
import { IconPlus } from '../components/Icons';
import { ProgressPhotos } from '../components/ProgressPhotos';
import { VolumeGrid } from '../components/RecoveryGrid';
import { Sheet } from '../components/Sheet';
import { getExercise } from '../data/exercises';
import { nutritionTargets, SPOT_REDUCTION_NOTE } from '../engine/nutrition';
import { recentPrs, sessionSetCount, sessionVolume } from '../engine/progression';
import { muscleStatuses } from '../engine/recovery';
import { formatRelative, minutesBetween, todayIso, weekKey } from '../lib/dates';
import { useApp } from '../store/AppStore';

export function Progress() {
  const { state, deleteWorkout } = useApp();
  const [logging, setLogging] = useState(false);
  const [showTruth, setShowTruth] = useState(false);
  const [openWorkout, setOpenWorkout] = useState<string | null>(null);

  const statuses = useMemo(() => muscleStatuses(state.workouts), [state.workouts]);
  const targets = useMemo(() => nutritionTargets(state.profile), [state.profile]);
  const prs = useMemo(() => recentPrs(state.workouts, 8), [state.workouts]);

  const weightPoints = useMemo(
    () =>
      [...state.metrics]
        .filter((m) => typeof m.weightKg === 'number')
        .sort((a, b) => (a.date < b.date ? -1 : 1))
        .slice(-24)
        .map((m) => ({ label: formatRelative(m.date), value: m.weightKg! })),
    [state.metrics],
  );

  const waistPoints = useMemo(
    () =>
      [...state.metrics]
        .filter((m) => typeof m.waistCm === 'number')
        .sort((a, b) => (a.date < b.date ? -1 : 1))
        .slice(-24)
        .map((m) => ({ label: formatRelative(m.date), value: m.waistCm! })),
    [state.metrics],
  );

  // Tonnage per week over the last 8 weeks.
  const volumeByWeek = useMemo(() => {
    const buckets = new Map<string, number>();
    for (const w of state.workouts) {
      if (!w.finishedAt) continue;
      const key = weekKey(w.date);
      buckets.set(key, (buckets.get(key) ?? 0) + sessionVolume(w));
    }
    return [...buckets.entries()]
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .slice(-8)
      .map(([key, value]) => ({ label: key.split('-')[1] ?? key, value: Math.round(value / 1000) }));
  }, [state.workouts]);

  const history = useMemo(
    () => [...state.workouts].filter((w) => w.finishedAt).sort((a, b) => (a.date < b.date ? 1 : -1)),
    [state.workouts],
  );

  const totalSessions = history.length;
  const totalVolume = history.reduce((n, w) => n + sessionVolume(w), 0);

  return (
    <div className="stack">
      <div className="grid-3">
        <div className="stat">
          <div className="label">Sessions</div>
          <div className="value">{totalSessions}</div>
        </div>
        <div className="stat">
          <div className="label">Tonnage</div>
          <div className="value">
            {Math.round(totalVolume / 1000)}
            <span className="unit"> t</span>
          </div>
        </div>
        <div className="stat">
          <div className="label">Weight</div>
          <div className="value">
            {state.profile.bodyweightKg}
            <span className="unit"> kg</span>
          </div>
        </div>
      </div>

      <button className="btn primary block" onClick={() => setLogging(true)}>
        <IconPlus /> Log weight & measurements
      </button>

      <div className="card">
        <div className="card-title">Bodyweight</div>
        <LineChart points={weightPoints} unit=" kg" />
      </div>

      {waistPoints.length >= 2 && (
        <div className="card">
          <div className="card-title">Waist</div>
          <LineChart points={waistPoints} unit=" cm" color="var(--info)" />
          <div className="tiny faint" style={{ marginTop: 8 }}>
            Waist tracks fat loss better than the scale does — water and food weight
            move the scale by a kilo or two daily, but your waist does not.
          </div>
        </div>
      )}

      <ProgressPhotos />

      <div className="card">
        <div className="card-title">Weekly tonnage (t)</div>
        <BarChart points={volumeByWeek} unit="t" />
      </div>

      <div className="card">
        <div className="card-title">Weekly sets by muscle</div>
        <VolumeGrid statuses={statuses} />
      </div>

      <div className="card">
        <div className="card-title">Daily targets</div>
        <div className="grid-2">
          <div className="stat">
            <div className="label">Calories</div>
            <div className="value">
              {targets.calories}
              <span className="unit"> kcal</span>
            </div>
          </div>
          <div className="stat">
            <div className="label">Protein</div>
            <div className="value">
              {targets.proteinG}
              <span className="unit"> g</span>
            </div>
          </div>
          <div className="stat">
            <div className="label">Fat</div>
            <div className="value">
              {targets.fatG}
              <span className="unit"> g</span>
            </div>
          </div>
          <div className="stat">
            <div className="label">Carbs</div>
            <div className="value">
              {targets.carbsG}
              <span className="unit"> g</span>
            </div>
          </div>
        </div>
        <div className="note small" style={{ marginTop: 11 }}>
          {targets.explanation}
        </div>
        <div className="tiny faint" style={{ marginTop: 8 }}>
          Maintenance is around {targets.tdee} kcal, so this is{' '}
          {targets.deficitOrSurplus >= 0 ? '+' : ''}
          {targets.deficitOrSurplus} kcal/day — roughly{' '}
          {Math.abs(targets.expectedWeeklyChangeKg)} kg per week
          {targets.expectedWeeklyChangeKg < 0 ? ' down' : targets.expectedWeeklyChangeKg > 0 ? ' up' : ''}.
          These are population estimates, not measurements of you; adjust from what the
          scale actually does over 2–3 weeks.
        </div>
        <button className="btn ghost sm block" style={{ marginTop: 11 }} onClick={() => setShowTruth(true)}>
          About targeting lower belly fat
        </button>
      </div>

      {prs.length > 0 && (
        <div className="card flush">
          <div className="card-title" style={{ padding: '14px 14px 8px', marginBottom: 0 }}>
            Recent records
          </div>
          {prs.map((pr, i) => {
            const ex = getExercise(pr.exerciseId);
            const gain = pr.previousE1rm ? pr.e1rm - pr.previousE1rm : 0;
            return (
              <div className="list-item" key={`${pr.exerciseId}-${pr.date}-${i}`}>
                <span className="pill accent">PR</span>
                <div className="grow">
                  <div style={{ fontWeight: 600 }}>{ex?.name ?? pr.exerciseId}</div>
                  <div className="tiny faint">
                    {pr.weightKg} kg × {pr.reps} · {formatRelative(pr.date)}
                  </div>
                </div>
                {gain > 0 && (
                  <span className="tiny mono" style={{ color: 'var(--ok)' }}>
                    +{Math.round(gain * 10) / 10} kg
                  </span>
                )}
              </div>
            );
          })}
          <div className="tiny faint" style={{ padding: '8px 14px 12px' }}>
            Measured by estimated 1RM, so beating a weight OR adding reps at the
            same weight both count.
          </div>
        </div>
      )}

      <div className="card flush">
        <div className="card-title" style={{ padding: '14px 14px 8px', marginBottom: 0 }}>
          Past workouts
        </div>
        {history.length === 0 && <div className="empty small">No finished workouts yet.</div>}
        {history.slice(0, 30).map((w) => (
          <div className="list-item" key={w.id} style={{ padding: 0 }}>
            <button
              className="list-item"
              style={{ borderBottom: 0 }}
              onClick={() => setOpenWorkout(w.id)}
            >
              <div className="grow">
                <div style={{ fontWeight: 600 }}>{w.templateName}</div>
                <div className="tiny faint">
                  {formatRelative(w.date)} · {sessionSetCount(w)} sets ·{' '}
                  {Math.round(sessionVolume(w)).toLocaleString('en-GB')} kg
                  {w.finishedAt ? ` · ${minutesBetween(w.startedAt, w.finishedAt)} min` : ''}
                  {w.isDeload ? ' · deload' : ''}
                </div>
              </div>
            </button>
            <button
              className="btn ghost sm"
              style={{ marginRight: 10 }}
              aria-label="Delete workout"
              onClick={() => {
                if (confirm(`Delete ${w.templateName} from ${w.date}?`)) deleteWorkout(w.id);
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      <WorkoutDetailSheet workoutId={openWorkout} onClose={() => setOpenWorkout(null)} />

      <MetricSheet open={logging} onClose={() => setLogging(false)} />

      <Sheet open={showTruth} title={SPOT_REDUCTION_NOTE.title} onClose={() => setShowTruth(false)}>
        {SPOT_REDUCTION_NOTE.body.map((para, i) => (
          <p className="small muted" key={i}>
            {para}
          </p>
        ))}
      </Sheet>
    </div>
  );
}

function WorkoutDetailSheet({
  workoutId,
  onClose,
}: {
  workoutId: string | null;
  onClose: () => void;
}) {
  const { state } = useApp();
  const workout = state.workouts.find((w) => w.id === workoutId);
  if (!workout) return null;

  const duration = workout.finishedAt
    ? minutesBetween(workout.startedAt, workout.finishedAt)
    : undefined;

  return (
    <Sheet open title={workout.templateName} onClose={onClose}>
      <div className="row wrap" style={{ gap: 6, marginBottom: 14 }}>
        <span className="pill">{formatRelative(workout.date)}</span>
        <span className="pill">{sessionSetCount(workout)} sets</span>
        <span className="pill">{Math.round(sessionVolume(workout)).toLocaleString('en-GB')} kg</span>
        {duration !== undefined && <span className="pill">{duration} min</span>}
        {workout.isDeload && <span className="pill warn">deload</span>}
      </div>

      <div className="stack" style={{ gap: 12 }}>
        {workout.exercises.map((le, i) => {
          const ex = getExercise(le.exerciseId);
          if (!ex) return null;
          return (
            <div key={`${le.exerciseId}-${i}`}>
              <div className="row between" style={{ marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{ex.name}</span>
                {le.swappedFrom && <span className="tiny faint">swapped</span>}
              </div>
              <div className="row wrap" style={{ gap: 5 }}>
                {le.sets.map((s, j) => (
                  <span className="pill mono" key={j}>
                    {s.weightKg || '–'} × {s.reps}
                    {s.rpe ? ` @${s.rpe}` : ''}
                  </span>
                ))}
              </div>
              {le.note && (
                <div className="tiny faint" style={{ marginTop: 4 }}>
                  {le.note}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {workout.notes && (
        <div className="note small" style={{ marginTop: 16 }}>
          {workout.notes}
        </div>
      )}
    </Sheet>
  );
}

function MetricSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { state, addMetric } = useApp();
  const [date, setDate] = useState(todayIso());
  const [weight, setWeight] = useState(String(state.profile.bodyweightKg));
  const [waist, setWaist] = useState('');
  const [chest, setChest] = useState('');
  const [arm, setArm] = useState('');
  const [thigh, setThigh] = useState('');
  const [notes, setNotes] = useState('');

  const num = (v: string) => (v.trim() === '' ? undefined : Number(v));

  const submit = () => {
    addMetric({
      date,
      weightKg: num(weight),
      waistCm: num(waist),
      chestCm: num(chest),
      armCm: num(arm),
      thighCm: num(thigh),
      notes: notes.trim() || undefined,
    });
    setWaist('');
    setChest('');
    setArm('');
    setThigh('');
    setNotes('');
    onClose();
  };

  return (
    <Sheet open={open} title="Log measurements" onClose={onClose}>
      <div className="stack">
        <div className="grid-2">
          <div className="field">
            <label htmlFor="m-date">Date</label>
            <input id="m-date" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="m-weight">Weight (kg)</label>
            <input id="m-weight" type="number" inputMode="decimal" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)} />
          </div>
        </div>

        <div className="grid-2">
          <div className="field">
            <label htmlFor="m-waist">Waist (cm)</label>
            <input id="m-waist" type="number" inputMode="decimal" step="0.5" value={waist} placeholder="at the navel" onChange={(e) => setWaist(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="m-chest">Chest (cm)</label>
            <input id="m-chest" type="number" inputMode="decimal" step="0.5" value={chest} placeholder="optional" onChange={(e) => setChest(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="m-arm">Arm (cm)</label>
            <input id="m-arm" type="number" inputMode="decimal" step="0.5" value={arm} placeholder="flexed" onChange={(e) => setArm(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="m-thigh">Thigh (cm)</label>
            <input id="m-thigh" type="number" inputMode="decimal" step="0.5" value={thigh} placeholder="optional" onChange={(e) => setThigh(e.target.value)} />
          </div>
        </div>

        <div className="field">
          <label htmlFor="m-notes">Notes</label>
          <textarea id="m-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="optional" />
        </div>

        <div className="note small">
          Weigh yourself first thing, after the toilet, before food, same conditions
          every time. Day-to-day swings of a kilo mean nothing — the weekly average
          is the number that matters.
        </div>

        <button className="btn primary lg block" onClick={submit}>
          Save
        </button>
      </div>
    </Sheet>
  );
}
